import React from 'react';
import { OrderResponse } from '../hooks/useOrders';
import { OrderStatus, PaymentStatus, ShipmentStatus } from '../components/StatusChips';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Callout } from '../components/ui/Callout';
import { Kicker } from '../components/ui/Kicker';
import { Icon } from '../components/ui/Icon';

export const STATUS_STEPS: { key: ShipmentStatus | 'created'; label: string }[] = [
  { key: 'created', label: 'Created' },
  { key: 'pending', label: 'Picked up' },
  { key: 'in_transit', label: 'In transit' },
  { key: 'out_for_delivery', label: 'Out for delivery' },
  { key: 'delivered', label: 'Delivered' },
];

export const stepIndex = (status: ShipmentStatus): number => {
  switch (status) {
    case 'delivered': return 4;
    case 'out_for_delivery': return 3;
    case 'in_transit': return 2;
    case 'ready':
    case 'scheduled_for_pickup': return 1;
    case 'pending': return 0;
    case 'exception':
    case 'cancelled': return -1;
    default: return 0;
  }
};

export const shipmentTone = (s: ShipmentStatus): 'ok' | 'info' | 'warn' | 'err' | 'default' => {
  switch (s) {
    case 'delivered': return 'ok';
    case 'in_transit':
    case 'out_for_delivery':
    case 'ready':
    case 'scheduled_for_pickup': return 'info';
    case 'pending': return 'warn';
    case 'exception':
    case 'cancelled': return 'err';
    default: return 'default';
  }
};

export const shipmentLabel = (s: ShipmentStatus): string =>
  s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export const getOrderStatusValues = (
  order: OrderResponse
): { orderStatus: OrderStatus; paymentStatus: PaymentStatus; shipmentStatus: ShipmentStatus } => {
  let orderStatus: OrderStatus = 'processing';
  let paymentStatus: PaymentStatus = 'pending';
  let shipmentStatus: ShipmentStatus = 'pending';
  if (!order.order_status || !order.payment_status || !order.shipment_status) {
    switch (order.status) {
      case 'pending':
        orderStatus = 'created'; paymentStatus = 'pending'; shipmentStatus = 'pending'; break;
      case 'processing':
        orderStatus = 'processing'; paymentStatus = 'paid'; shipmentStatus = 'pending'; break;
      case 'shipped':
        orderStatus = 'processing'; paymentStatus = 'paid'; shipmentStatus = 'in_transit'; break;
      case 'delivered':
        orderStatus = 'completed'; paymentStatus = 'paid'; shipmentStatus = 'delivered'; break;
      case 'cancelled':
        orderStatus = 'cancelled';
        paymentStatus = order.paid_at ? 'refunded' : 'failed';
        shipmentStatus = 'cancelled';
        break;
    }
  }
  return {
    orderStatus: order.order_status || orderStatus,
    paymentStatus: order.payment_status || paymentStatus,
    shipmentStatus: order.shipment_status || shipmentStatus,
  };
};

export const formatOrderDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'Invalid date';
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

export const AddressBlock: React.FC<{ title: string; address: any }> = ({ title, address }) => {
  if (!address) return null;
  const cityState = [address.city?.name, address.state?.name, address.zipCode]
    .filter(Boolean)
    .join(', ');
  return (
    <Card pad>
      <Kicker>{title}</Kicker>
      <div style={{ fontWeight: 600, marginTop: 8 }}>{address.contactName}</div>
      {address.companyName && (
        <div className="muted" style={{ fontSize: 13 }}>{address.companyName}</div>
      )}
      <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
        <div>
          {address.street}
          {address.houseNo ? `, ${address.houseNo}` : ''}
        </div>
        <div>{cityState}</div>
        <div>
          {address.country?.name} {address.country?.emoji}
        </div>
      </div>
      {(address.email || address.phone) && (
        <div className="muted" style={{ fontSize: 13, marginTop: 10, lineHeight: 1.6 }}>
          {address.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="mail" size={13} /> {address.email}
            </div>
          )}
          {address.phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="phone" size={13} /> {address.phone}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export const StatusTimeline: React.FC<{ shipmentStatus: ShipmentStatus }> = ({ shipmentStatus }) => {
  const idx = stepIndex(shipmentStatus);
  if (shipmentStatus === 'cancelled' || shipmentStatus === 'exception') {
    return (
      <Callout tone="warn">
        <Icon name="alertCircle" size={16} />
        <span>Shipment {shipmentLabel(shipmentStatus).toLowerCase()}.</span>
      </Callout>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 0 }}>
      {STATUS_STEPS.map((step, i) => (
        <div key={step.key} style={{ flex: 1, textAlign: 'center' }}>
          <div
            style={{
              height: 4,
              background: i <= idx ? 'var(--brand-blue)' : 'var(--line)',
              borderRadius:
                i === 0 ? '99px 0 0 99px' : i === STATUS_STEPS.length - 1 ? '0 99px 99px 0' : 0,
            }}
          />
          <div
            style={{
              fontSize: 12,
              color: i === idx ? 'var(--ink)' : 'var(--ink-3)',
              fontWeight: i === idx ? 600 : 500,
              marginTop: 8,
            }}
          >
            {step.label}
          </div>
        </div>
      ))}
    </div>
  );
};

export const ParcelItemsRow: React.FC<{ items: any[]; currency: string }> = ({ items, currency }) => {
  if (!items?.length) return null;
  const total = items.reduce(
    (sum: number, item: any) => sum + (item.price || 0) * (item.quantity || 1),
    0
  );
  return (
    <tr>
      <td colSpan={5} style={{ padding: 0, borderBottom: 0 }}>
        <div style={{ margin: 12, padding: 12, background: 'var(--bg-tint)', borderRadius: 8 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 8,
              color: 'var(--brand-blue)',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <Icon name="box" size={14} />
            <span>
              Package contents ({items.length} {items.length === 1 ? 'item' : 'items'})
            </span>
          </div>
          <table className="table" style={{ background: 'var(--bg-elev)', borderRadius: 6 }}>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>Description</th>
                <th>Origin</th>
                <th style={{ textAlign: 'center' }}>Weight</th>
                <th style={{ textAlign: 'center' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item: any, i: number) => (
                <tr key={item.id || `it-${i}`}>
                  <td style={{ fontWeight: 500 }}>{item.description || 'Unlabeled item'}</td>
                  <td>
                    <Badge>{item.madeIn || 'N/A'}</Badge>
                  </td>
                  <td style={{ textAlign: 'center' }} className="mono">{item.weight} kg</td>
                  <td style={{ textAlign: 'center' }} className="mono">{item.quantity}</td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                    {item.price > 0 ? `${item.price} ${currency}` : 'N/A'}
                  </td>
                </tr>
              ))}
              <tr style={{ background: 'var(--bg-tint)' }}>
                <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>Total</td>
                <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>
                  {total} {currency}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
};

export const buildPaymentStateFromOrder = (order: OrderResponse) => {
  const orderAny = order as unknown as Record<string, any>;
  const parcels = (order.parcels || []).map((parcel) => {
    const dimensionsDisplay =
      (parcel as any).dimensionsDisplay ||
      (parcel.length && parcel.width && parcel.height
        ? `${parcel.length} × ${parcel.width} × ${parcel.height} cm`
        : 'Dimensions not available');
    return {
      ...parcel,
      items: parcel.items || [],
      showPrice: typeof parcel.showPrice === 'boolean' ? parcel.showPrice : true,
      dimensionsDisplay,
    };
  });
  const totalWeight = parcels.reduce((t, p) => t + (p.weight || 0) * (p.quantity ?? 1), 0);
  const totalItems = parcels.reduce((t, p) => t + (p.quantity || 0), 0);
  const shippingCost =
    orderAny.shipping_cost ??
    order.payment_details?.shipping ??
    order.selected_rate?.price ??
    order.total_price ??
    0;
  const insuranceCost = order.payment_details?.insurance ?? orderAny.insurance_cost ?? 0;
  const taxCost = order.payment_details?.tax ?? orderAny.tax ?? orderAny.tax_cost ?? 0;
  const totalCost = order.total_price ?? shippingCost + insuranceCost + taxCost;
  const selectedRate = order.selected_rate
    ? {
        ...order.selected_rate,
        deliveryTime:
          (order.selected_rate as any).deliveryTime || order.delivery_estimate,
        provider_display_name:
          (order.selected_rate as any)?.provider_display_name ||
          order.selected_rate.provider ||
          order.provider,
      }
    : {
        id: `rate-${order.id}`,
        name: order.service,
        provider: order.provider,
        carrier: order.carrier,
        price: shippingCost,
        currency: order.currency,
        deliveryTime: order.delivery_estimate,
      };
  return {
    orderId: order.id,
    order_status: order.order_status,
    status: order.status,
    allowDraft: (order.order_status || order.status) !== 'draft',
    shippingType: order.is_document ? 'document' : 'package',
    addresses: { from: order.from_address, to: order.to_address },
    collectionDetails:
      orderAny.collection_details ||
      orderAny.collectionDetails || {
        date: null,
        timeFrom: null,
        timeTo: null,
        instructions: '',
      },
    parcels,
    documents: order.documents || [],
    selectedRate,
    price: { shipping: shippingCost, insurance: insuranceCost, tax: taxCost, total: totalCost },
    parcel: {
      weight: `${totalWeight.toFixed(2)} kg`,
      dimensions: parcels[0]?.dimensionsDisplay || 'Dimensions not available',
    },
    carrier: order.carrier || order.provider,
    service: order.service || order.selected_rate?.name || 'Service',
    deliveryEstimate: order.delivery_estimate,
    currency: order.currency || 'ILS',
    isDocument: order.is_document,
    orderDetails: {
      totalWeight,
      totalItems,
      estimatedDelivery: order.delivery_estimate,
      service: order.service || order.selected_rate?.name || '',
    },
    default_shipping_id: orderAny.default_shipping_id ?? null,
    default_shipping_name: orderAny.default_shipping_name ?? null,
    shipmentReason: orderAny.shipment_reason ?? '',
    signatureRequired: orderAny.signature_required ?? false,
    vatType: orderAny.vat_type ?? '',
    vatNumber: orderAny.vat_number ?? '',
    referenceNumber:
      order.reference_number ||
      order.referenceNumber ||
      `REF-${order.order_number || order.id}`,
  };
};
